import React, { useState, useCallback, useContext, useMemo, useRef, useEffect } from "react";
import {
  Box,
  Tabs,
  Tab,
  Button,
  TextField,
  InputAdornment,
  Typography,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import DeductionApprovalTab from "./DeductionApprovalTab";
import DeductionRunTab from "./DeductionRunTab";
import Reports from "../../Reports";
import ProcessPayoutConfirmation from "../ProcessPayoutConfirmation";
import {
  GREEN_FILLED_BUTTON_STYLE,
  TABS_STYLE,
  TAB_ITEM_STYLE,
  SEARCH_FIELD_STYLE,
} from "../../../styles/CommonStyles";
import SharedDataContext from "../../../_helper/SharedData";
import { processPayoutDeduction, getPendingSettlements, exportDriverSettlements } from "../../../Services/DriverDeduction.service";
import { usePayoutAllowedCities } from "../../../hooks/usePayoutAllowedCities";
import { toast } from "react-toastify";
import moment from "moment";

/**
 * Main Driver Deduction Approval screen.
 * Two tabs: "Deduction Approval" and "Driver Deduction Run"
 */
const DriverDeductionApproval = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const searchDebounceRef = useRef(null);
  // selectedRows: array of { driverId, periodFrom, periodTo } objects
  const [selectedRows, setSelectedRows] = useState([]);
  // selectAll: true when header checkbox selects all (clears individual ids)
  const [selectAll, setSelectAll] = useState(false);
  const [isPayoutModalOpen, setIsPayoutModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [approvalRefreshKey, setApprovalRefreshKey] = useState(false);
  const [deductionRunRefreshKey, setDeductionRunRefreshKey] = useState(false);
  const refreshKeyRef = useRef(false);

  const [downloading, setDownloading] = useState(false);
  const [totalPendingCount, setTotalPendingCount] = useState(0);
  const { globalCityField } = useContext(SharedDataContext);
  const serviceCityId = globalCityField.value?.id || null;

  const { allowedCitiesLoading, isCityAllowed, allowedCityNamesMsg } = usePayoutAllowedCities();

  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
    setSelectedRows([]);
    setSelectAll(false);
    setSearchQuery("");
    setSearchInputValue("");
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
  }, []);

  // Cooldown guard — prevents rapid clicks from toggling key back-and-forth and firing redundant fetches.
  const handleRunTabRefresh = useCallback(() => {
    if (refreshKeyRef.current) return;
    refreshKeyRef.current = true;
    setDeductionRunRefreshKey((prev) => !prev);
    setTimeout(() => { refreshKeyRef.current = false; }, 1000);
  }, []);

  const handleSearchChange = useCallback((event) => {
    const inputValue = event.target.value;
    setSearchInputValue(inputValue);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchQuery(inputValue);
    }, 400);
  }, []);

  // Derive fromDate / toDate from selectedRows
  const fromDate = useMemo(
    () => selectedRows.reduce((min, r) => (!min || r.periodFrom < min ? r.periodFrom : min), null),
    [selectedRows]
  );
  const toDate = useMemo(
    () => selectedRows.reduce((max, r) => (!max || r.periodTo > max ? r.periodTo : max), null),
    [selectedRows]
  );

  /**
   * Called by DeductionApprovalTab when selection changes.
   * @param {{ driverId, periodFrom, periodTo }[]} rows
   * @param {boolean} isSelectAll
   */
  const handleSelectionChange = useCallback((rows, isSelectAll) => {
    setSelectedRows(rows);
    setSelectAll(isSelectAll);
  }, []);

  const handleProcessPayoutClick = useCallback(() => {
    setIsPayoutModalOpen(true);
  }, []);

  const handlePayoutModalClose = useCallback(() => {
    setIsPayoutModalOpen(false);
  }, []);

  const handlePayoutConfirm = useCallback(async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      await processPayoutDeduction({
        serviceCityId,
        selectAll,
        driverIds: selectAll ? [] : selectedRows.map((r) => r.driverId),
        fromDate,
        toDate,
      });

      toast.success("Payout deduction job initiated successfully");
      setIsPayoutModalOpen(false);
      setSelectedRows([]);
      setSelectAll(false);
      setApprovalRefreshKey((prev) => !prev); // Trigger table refresh
    } catch (err) {
      console.error("Process payout failed", err);
      toast.error(err?.response?.data?.message || "Failed to process payout. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  }, [serviceCityId, selectAll, selectedRows, fromDate, toDate]);

  const handleDownloadCSV = useCallback(async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      await exportDriverSettlements({
        settlementStatus: "PENDING",
        serviceCityId: serviceCityId || undefined,
      });
      toast.success("CSV downloaded successfully");
    } catch (err) {
      console.error("CSV Download failed", err);
      toast.error(err.message || "Failed to download CSV.");
    } finally {
      setTimeout(() => setDownloading(false), 2000);
    }
  }, [serviceCityId, downloading]);

  const hasSelection = selectAll || selectedRows.length > 0;

  if (allowedCitiesLoading) {
    return (
      <Box sx={{ padding: "20px 10px", mt: 10, height: "100%", width: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {isCityAllowed ? (
        <Box sx={{ width: "100%" }}>
          {/* Tabs Section */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 3,
              pt: 3,
            }}
          >
            <Tabs value={activeTab} onChange={handleTabChange} sx={TABS_STYLE}>
              <Tab label="Deduction Approval" sx={TAB_ITEM_STYLE} disableRipple />
              <Tab label="Duty Register Report" sx={TAB_ITEM_STYLE} disableRipple />
              <Tab label="Driver Deduction Run" sx={TAB_ITEM_STYLE} disableRipple />
            </Tabs>

            {/* Refresh button — visible only on Driver Deduction Run tab */}
            {activeTab === 2 && (
              <Button
                size="small"
                variant="outlined"
                onClick={handleRunTabRefresh}
                endIcon={<RefreshIcon fontSize="medium" />}
                sx={{
                  textTransform: 'none',
                  borderColor: '#888888ff',
                  color: '#626262',
                  fontSize: '1.05rem',
                  px: 1.5,
                  py: 0.6,
                  mt: 0.8,
                  '&:hover': { borderColor: '#39B54A', color: '#FFF', backgroundColor: '#39B54A' },
                }}
              >
                Refresh
              </Button>
            )}
          </Box>

          {/* Action Bar — only on Approval tab */}
          {activeTab === 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              {/* Left: Search */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TextField
                  size="small"
                  placeholder="Search by Cab No."
                  value={searchInputValue}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "#9e9e9e" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={SEARCH_FIELD_STYLE}
                />
                {/* <IconButton
                  sx={{
                    border: "1px solid #D9D9D9",
                    borderRadius: "0.42vw",
                    padding: "0.56vw",
                  }}
                >
                  <TuneIcon sx={{ color: "#626262" }} />
                </IconButton> */}
              </Box>

              {/* Right: Process Payout */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleDownloadCSV}
                  disabled={downloading || totalPendingCount === 0}
                  sx={GREEN_FILLED_BUTTON_STYLE}
                  startIcon={downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
                >
                  {downloading ? "Preparing..." : "Download CSV"}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleProcessPayoutClick}
                  sx={GREEN_FILLED_BUTTON_STYLE}
                  disabled={!hasSelection}
                >
                  Process Payout
                </Button>
              </Box>
            </Box>
          )}

          {/* Tab Content */}
          {activeTab === 0 && (
            <DeductionApprovalTab
              searchQuery={searchQuery}
              serviceCityId={serviceCityId}
              selectedRows={selectedRows}
              selectAll={selectAll}
              onSelectionChange={handleSelectionChange}
              onTotalCountChange={setTotalPendingCount}
              refreshKey={approvalRefreshKey}
            />
          )}
          {activeTab === 1 && (
            <Box sx={{ "& > div": { padding: "0px !important" } }}>
              <Reports link="https://app.powerbi.com/reportEmbed?reportId=204611e6-1048-4193-ac6a-4c747f514552&autoAuth=true&ctid=f6207f8b-59d2-47ed-9ba2-8484d0dd3061" />
            </Box>
          )}
          {activeTab === 2 && (
            <DeductionRunTab
              refreshKey={deductionRunRefreshKey}
            />
          )}

          {/* Process Payout Confirmation */}
          <ProcessPayoutConfirmation
            open={isPayoutModalOpen}
            onClose={handlePayoutModalClose}
            onConfirm={handlePayoutConfirm}
            isProcessing={isProcessing}
            selectedCount={selectedRows.length}
            selectAll={selectAll}
          />
        </Box>
      ) : (
        <Box sx={{ padding: "20px 10px", mt: 10, height: "100%", width: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Typography variant="h6" color="text.secondary">
            This Service is only available for {allowedCityNamesMsg}
          </Typography>
        </Box>
      )}
    </>
  );
};

export default DriverDeductionApproval;
